import { ITeamResponseSchema, TeamTypeEnum } from '@monetize/types/app';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MBox,
  MHeading,
  MTable,
  MTag,
  MTbody,
  MTd,
  MText,
  MTh,
  MThead,
  MTr,
} from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import * as ROLES from '../../../constants/roles';
import { TEAM_TYPE_DISPLAY } from '../../../constants/teams';
import { useAuth } from '../../../services/auth0';
import { sortByAlphabetically } from '../../../utils';

interface UserTeamsProps {
  teams: ITeamResponseSchema[];
  /** The profile's own user id, used to work out owner vs member. */
  userId?: string;
}

/**
 * The teams this user belongs to. Deliberately just the facts - name, type,
 * size, and whether this user owns the team. The full membership lives on the
 * team detail page, which is linked for anyone allowed to open it.
 */
export const UserTeams = ({ teams, userId }: UserTeamsProps) => {
  const { currentTenantUserHasRole } = useAuth();

  // Copy before sorting: the array belongs to the fetched user record.
  const sortedTeams = useMemo(
    () => [...(teams || [])].sort(sortByAlphabetically('name')),
    [teams],
  );

  if (!sortedTeams.length) {
    return null;
  }

  // Gate on the same roles that guard the /settings/teams routes, so the link
  // can never lead somewhere the user is bounced out of.
  const canOpenTeamDetails = currentTenantUserHasRole(
    ROLES.SETTINGS_TEAMS_ROLES,
  );

  return (
    <MBox w="full">
      <MHeading size="xs" fontWeight="semibold" mb={2}>
        Teams
      </MHeading>
      <MBox maxW="40rem">
        <MTable variant="line" size="sm">
          <MThead>
            <MTr>
              <MTh>Name</MTh>
              <MTh>Type</MTh>
              <MTh textAlign="end">Members</MTh>
              <MTh>Role</MTh>
            </MTr>
          </MThead>
          <MTbody>
            {sortedTeams.map(({ id, name, type, users, owner }) => {
              const isOwner = owner === userId;
              return (
                <MTr key={id} data-testid={`user-team-row-${id}`}>
                  <MTd>
                    {canOpenTeamDetails ? (
                      <Link to={ROUTES.getTeamsDetailsRoute(id)}>
                        <MText color="tIndigo.base" truncate>
                          {name}
                        </MText>
                      </Link>
                    ) : (
                      <MText truncate>{name}</MText>
                    )}
                  </MTd>
                  {/* TEAM_TYPE_DISPLAY, not TEAM_TYPE_ENUM_DISPLAY: the
                      backend still returns LEGACY on old teams, which the
                      enum-only map renders as blank. */}
                  <MTd>{TEAM_TYPE_DISPLAY[type as TeamTypeEnum] ?? type}</MTd>
                  <MTd textAlign="end">{users?.length ?? 0}</MTd>
                  <MTd>
                    {isOwner ? (
                      <MTag size="sm" variant="blue">
                        Owner
                      </MTag>
                    ) : (
                      <MText color="fg.muted">Member</MText>
                    )}
                  </MTd>
                </MTr>
              );
            })}
          </MTbody>
        </MTable>
      </MBox>
    </MBox>
  );
};
